/* eslint-disable no-nested-ternary */
import React, {
  useState, useCallback, useMemo, useEffect, useLayoutEffect, useRef,
} from 'react';
import { useDrag } from 'ahooks';
import cn from 'classnames';
import { useCardExpo } from '@/hooks/useCardExpo';
import type { MediaLibraryItemSaveParams, MediaLibraryMaterialType } from '$/api/server.types';
import IconFont from '$/components/IconFont';
import NextImage, { getThumbnailUrl } from '$/components/NextImage';
import { message } from '$/components/Message';
import { getImageExtensionFromUrl } from '$/utils';
import { getVideoCoverUrl } from '$/utils/upload';
import {
  VIDEO_COVER_FALLBACK_URL,
} from '$/pages/RoboneoTeamFlow/nodes/VideoNode/utils';
import { useLanguageStore } from '$/store/languageStore';
import type { PreviewHeaderMenuSlotProps, PreviewMediaItem } from '$/pages/RoboneoChatBoard/type';
import { assetDisplayTitle } from '$/utils/assetLibraryCardLayout';
import styles from '../index.module.scss';
import type {
  AssetModalFolderItem, AssetItemView, AssetModalMediaCoverProps, AssetModalRawItem,
} from '../type';
import { DISABLED_ASSET_CANVAS_DRAG_PAYLOAD } from '../utils';
import {
  buildRoboneoHistoryPreviewSaveStatParams,
  buildTeamAssetHistorySaveStatParams,
} from '../tracking';

export type FolderItem = AssetModalFolderItem;
export type AssetPreviewSaveLocation =
  | 'asset_preview_page'
  | 'history_preview_page'
  | 'team_asset'
  | 'team_history';
export const DEFAULT_PAGE_SIZE = 50;
export const MEDIA_LIBRARY_MAX_NAME_LENGTH = 20;
export type MediaLibraryUploadMaterialType = Extract<MediaLibraryMaterialType, 'image' | 'video' | 'audio'>;
export const MEDIA_LIBRARY_HISTORY_UPLOAD_LIMITS: Record<MediaLibraryUploadMaterialType, number> = {
  image: 10,
  video: 1,
  audio: 1,
};
export const MEDIA_LIBRARY_AUDIO_WAVEFORM_BARS = [
  { id: 'audio-bar-01', height: 10 },
  { id: 'audio-bar-02', height: 18 },
  { id: 'audio-bar-03', height: 12 },
  { id: 'audio-bar-04', height: 28 },
  { id: 'audio-bar-05', height: 24 },
  { id: 'audio-bar-06', height: 20 },
  { id: 'audio-bar-07', height: 22 },
  { id: 'audio-bar-08', height: 30 },
  { id: 'audio-bar-09', height: 18 },
  { id: 'audio-bar-10', height: 28 },
  { id: 'audio-bar-11', height: 26 },
  { id: 'audio-bar-12', height: 33 },
  { id: 'audio-bar-13', height: 38 },
  { id: 'audio-bar-14', height: 30 },
  { id: 'audio-bar-15', height: 30 },
  { id: 'audio-bar-16', height: 30 },
  { id: 'audio-bar-17', height: 26 },
  { id: 'audio-bar-18', height: 26 },
  { id: 'audio-bar-19', height: 18 },
  { id: 'audio-bar-20', height: 28 },
  { id: 'audio-bar-21', height: 33 },
  { id: 'audio-bar-22', height: 24 },
  { id: 'audio-bar-23', height: 33 },
  { id: 'audio-bar-24', height: 28 },
  { id: 'audio-bar-25', height: 33 },
  { id: 'audio-bar-26', height: 38 },
  { id: 'audio-bar-27', height: 33 },
  { id: 'audio-bar-28', height: 18 },
  { id: 'audio-bar-29', height: 8 },
] as const;

/** 按个人或团队预览位置组装公共下载埋点扩展字段。 */
function buildAssetPreviewSaveTrackingProps(
  item: AssetItemView,
  canvasSaveLocation: AssetPreviewSaveLocation,
): Pick<PreviewMediaItem, 'canvas_save_stat_params' | 'canvas_save_success_stat_params'> {
  const isTeamLocation = canvasSaveLocation === 'team_asset'
    || canvasSaveLocation === 'team_history';
  const isHistoryLocation = canvasSaveLocation === 'history_preview_page'
    || canvasSaveLocation === 'team_history';
  return {
    ...(isTeamLocation
      ? {
        canvas_save_stat_params: buildTeamAssetHistorySaveStatParams(
          item,
          canvasSaveLocation,
        ),
      }
      : {}),
    ...(isHistoryLocation
      ? { canvas_save_success_stat_params: buildRoboneoHistoryPreviewSaveStatParams(item) }
      : {}),
  };
}
export interface AssetModalConfirmDialogProps {
  visible: boolean;
  title: string;
  note: string;
  confirmLabel: string;
  onClose: () => void;
  onConfirm: () => void;
}
export interface AssetModalMoveDialogProps {
  visible: boolean;
  folders: FolderItem[];
  targetFolderId?: number;
  overlayZIndex?: number;
  disabledFolderReasons?: Map<number, string>;
  createFolderDisabledReason?: string;
  /** 弹窗标题，缺省用「移动到」。 */
  title?: string;
  /** 确认按钮文案，缺省用通用「确认」。 */
  confirmLabel?: string;
  onTargetFolderChange: (folderId: number) => void;
  /** 不传则不展示「新建文件夹」入口（如历史记录批量入库只选已有文件夹）。 */
  onCreateFolder?: () => void;
  onClose: () => void;
  onSubmit: () => void;
}
/**
 * 判断素材卡片是否能复用全局图片预览弹框。
 */
export function isAssetImagePreviewable(item: AssetItemView): boolean {
  return item.materialType === 'image' && Boolean((item.coverUrl || item.mediaUrl).trim());
}
/**
 * 判断素材卡片是否能复用全局文本/脚本预览弹框。
 */
export function isAssetDocumentTextPreviewable(item: AssetItemView): boolean {
  return ['text', 'script'].includes(item.materialType) && Boolean(item.mediaUrl.trim());
}
/**
 * 读取素材的有水印地址，兼容历史记录顶层字段和资产库 ext 回填字段。
 */
export function getAssetWatermarkUrl(item: AssetItemView): string {
  const rawWithCamelCase = item.raw as AssetModalRawItem & {
    watermarkUrl?: string;
  };
  return (item.raw.watermark_url || rawWithCamelCase.watermarkUrl || '').trim();
}
/**
 * 读取素材的视频水印封面地址，缺省时由调用方按主水印视频生成封面。
 */
export function getAssetWatermarkCoverUrl(item: AssetItemView): string {
  return (item.raw.watermark_cover_url || item.raw.watermarkCoverUrl || '').trim();
}
/**
 * 读取视频压缩地址，兼容历史记录、资产库 ext 回填和历史记录接口字段。
 */
export function getAssetVideoCompressUrl(item: AssetItemView): string {
  const raw = item.raw as AssetModalRawItem & {
    compressUrl?: string;
  };
  return (raw.compress_url || raw.compressUrl || '').trim();
}
/**
 * 读取视频水印压缩地址，供历史预览添加到输入框和保存到素材库时透传。
 */
export function getAssetVideoWatermarkCompressUrl(item: AssetItemView): string {
  const raw = item.raw as AssetModalRawItem & {
    watermarkCompressUrl?: string;
  };
  return (raw.watermark_compress_url || raw.watermarkCompressUrl || '').trim();
}
/**
 * 读取视频封面图；历史记录缺缩略图时可能把视频 URL 写进 coverUrl，需要改用 vframe 兜底。
 */
export function getAssetVideoPosterUrl(item: AssetItemView): string {
  const mediaUrl = item.mediaUrl.trim();
  const coverUrl = (item.coverUrl || '').trim();
  if (coverUrl && coverUrl !== mediaUrl) {
    return coverUrl;
  }
  return mediaUrl ? getVideoCoverUrl(mediaUrl) : '';
}

/**
 * 资产弹层视频卡片封面缩略图地址：vframe 截帧走 /w/320，其余走 imageView 缩略图。
 */
export function resolveAssetModalVideoCoverSrc(item: AssetItemView): string {
  const posterUrl = getAssetVideoPosterUrl(item);
  if (posterUrl.includes('vframe/jpg/offset')) {
    return posterUrl.includes('/w/')
      ? posterUrl
      : posterUrl.replace(/(vframe\/jpg\/offset\/[\d.]+)/, '$1/w/320');
  }
  return getThumbnailUrl(posterUrl, 320);
}
/**
 * 判断发送到画布入口下的卡片点击是否应打开预览，而不是插入画布。
 */
export function isAssetCardClickPreviewable(item: AssetItemView): boolean {
  return isAssetImagePreviewable(item) || isAssetVideoPreviewable(item) || isAssetDocumentTextPreviewable(item);
}
/**
 * 将资产弹框卡片转换为全局 MediaPreview 的图片预览项，预览使用原图地址以保留鉴权参数。
 */
export function buildAssetImagePreviewItem(
  item: AssetItemView,
  canvasSaveLocation: AssetPreviewSaveLocation = 'asset_preview_page',
): PreviewMediaItem | null {
  if (!isAssetImagePreviewable(item)) return null;
  const previewUrl = (item.mediaUrl || item.coverUrl).trim();
  const watermarkUrl = getAssetWatermarkUrl(item);
  return {
    type: 'image',
    task_id: String(item.raw.source_id || item.raw.id || item.id),
    url: previewUrl,
    width: item.raw.width ?? item.raw.image?.width,
    height: item.raw.height ?? item.raw.image?.height,
    name: assetDisplayTitle(item.raw, item.mediaUrl, item.id),
    watermarkUrl: watermarkUrl || undefined,
    canvas_save_location: canvasSaveLocation,
    ...buildAssetPreviewSaveTrackingProps(item, canvasSaveLocation),
    asset_id: String(item.id),
  };
}
/**
 * 将文本/脚本素材转换为全局 MediaPreview 的文档预览项。
 */
export function buildAssetDocumentTextPreviewItem(
  item: AssetItemView,
  canvasSaveLocation: AssetPreviewSaveLocation = 'asset_preview_page',
): PreviewMediaItem | null {
  if (!isAssetDocumentTextPreviewable(item)) return null;
  return {
    type: 'image',
    previewKind: item.materialType === 'script' ? 'script' : 'text',
    task_id: String(item.raw.source_id || item.raw.id || item.id),
    url: item.mediaUrl.trim(),
    name: assetDisplayTitle(item.raw, item.mediaUrl, item.id),
    canvas_save_location: canvasSaveLocation,
    ...buildAssetPreviewSaveTrackingProps(item, canvasSaveLocation),
    asset_id: String(item.id),
  };
}
/**
 * 判断素材卡片是否能复用全局视频预览弹框，视频预览必须有原始播放地址。
 */
export function isAssetVideoPreviewable(item: AssetItemView): boolean {
  return item.materialType === 'video' && Boolean(item.mediaUrl.trim());
}
/**
 * 将资产弹框卡片转换为全局 MediaPreview 的视频预览项，并要求弹框打开后立即起播。
 */
export function buildAssetVideoPreviewItem(
  item: AssetItemView,
  canvasSaveLocation: AssetPreviewSaveLocation = 'asset_preview_page',
): PreviewMediaItem | null {
  if (!isAssetVideoPreviewable(item)) return null;
  const url = item.mediaUrl.trim();
  const watermarkUrl = getAssetWatermarkUrl(item);
  const watermarkCoverUrl = getAssetWatermarkCoverUrl(item);
  const compressUrl = getAssetVideoCompressUrl(item);
  const watermarkCompressUrl = getAssetVideoWatermarkCompressUrl(item);
  const coverUrl = getAssetVideoPosterUrl(item);
  // vframe 截帧 URL 不支持 |imageView2 管道缩放，需用 vframe 自带的 /w/ 参数；
  // /w/320 必须紧跟 vframe/jpg/offset/N 后面，不能拼到 URL 末尾（否则被签名参数隔开不生效）。
  const thumbnailCover = coverUrl.includes('vframe/jpg/offset')
    ? coverUrl.includes('/w/')
      ? coverUrl
      : coverUrl.replace(/(vframe\/jpg\/offset\/[\d.]+)/, '$1/w/320')
    : getThumbnailUrl(coverUrl, 320);
  const rawWatermarkCover = watermarkCoverUrl || (watermarkUrl ? getVideoCoverUrl(watermarkUrl) : undefined);
  // eslint-disable-next-line no-nested-ternary
  const thumbnailWatermarkCover = rawWatermarkCover
    ? rawWatermarkCover.includes('vframe/jpg/offset')
      ? rawWatermarkCover.includes('/w/')
        ? rawWatermarkCover
        : rawWatermarkCover.replace(/(vframe\/jpg\/offset\/[\d.]+)/, '$1/w/320')
      : getThumbnailUrl(rawWatermarkCover, 320)
    : undefined;
  return {
    type: 'video',
    task_id: String(item.raw.source_id || item.raw.id || item.id),
    url,
    watermarkUrl: watermarkUrl || undefined,
    cover: thumbnailCover,
    watermarkCoverUrl: thumbnailWatermarkCover,
    width: item.raw.width ?? item.raw.video?.width,
    height: item.raw.height ?? item.raw.video?.height,
    name: assetDisplayTitle(item.raw, item.mediaUrl, item.id),
    extra: {
      media_url: url,
      ...(compressUrl ? { media_url_by_compress: compressUrl } : {}),
      ...(watermarkCompressUrl ? { media_url_by_water_mark_compress: watermarkCompressUrl } : {}),
    },
    canvas_save_location: canvasSaveLocation,
    ...buildAssetPreviewSaveTrackingProps(item, canvasSaveLocation),
    asset_id: String(item.id),
    videoPlaybackHandoff: {
      startSec: 0,
      shouldPlay: true,
    },
  };
}
/**
 * 将图片/视频素材统一转换为公共 MediaPreview 列表项；文本/脚本不进入列表，避免文档预览展示底部列表。
 */
export function buildAssetMediaPreviewItem(
  item: AssetItemView,
  canvasSaveLocation: AssetPreviewSaveLocation = 'asset_preview_page',
): PreviewMediaItem | null {
  return buildAssetImagePreviewItem(item, canvasSaveLocation) ?? buildAssetVideoPreviewItem(item, canvasSaveLocation);
}
/**
 * 按当前可见素材顺序构造图片/视频预览列表，并返回当前素材在列表中的索引。
 */
export function buildAssetMediaPreviewList(
  items: AssetItemView[],
  currentItem: AssetItemView,
  canvasSaveLocation: AssetPreviewSaveLocation = 'asset_preview_page',
) {
  const previewPairs = items
    .map((item) => ({ item, previewItem: buildAssetMediaPreviewItem(item, canvasSaveLocation) }))
    .filter(
      (
        pair,
      ): pair is {
        item: AssetItemView;
        previewItem: PreviewMediaItem;
      } => Boolean(pair.previewItem),
    );
  const currentIndex = Math.max(
    0,
    previewPairs.findIndex((pair) => pair.item.id === currentItem.id),
  );
  return {
    previewItems: previewPairs.map((pair) => pair.previewItem),
    sourceItems: previewPairs.map((pair) => pair.item),
    currentIndex,
  };
}
export type AssetPreviewAddToCanvasSlotProps = Partial<PreviewHeaderMenuSlotProps> & {
  compact?: boolean;
  item: AssetItemView;
  previewItems?: AssetItemView[];
  label: string;
  onAddToCanvas: (item: AssetItemView) => void | Promise<void>;
};
/**
 * 资产库预览弹框内的“添加到画布/工作流”按钮，复用卡片显式添加逻辑。
 */
export function AssetPreviewAddToCanvasSlot({
  compact = false,
  item,
  previewItems,
  medias,
  current = 0,
  label,
  closePreview: closePreviewSlot,
  onAddToCanvas,
}: AssetPreviewAddToCanvasSlotProps): React.ReactElement {
  const [adding, setAdding] = useState(false);
  const currentAssetId = medias?.[current]?.asset_id;
  const activeItem = previewItems?.find((previewItem) => String(previewItem.id) === currentAssetId) ?? item;
  /** 点击预览弹框添加按钮时，按当前预览项触发显式添加并关闭预览。 */
  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (adding) return;
    setAdding(true);
    try {
      await onAddToCanvas(activeItem);
      closePreviewSlot?.();
    } finally {
      setAdding(false);
    }
  };
  return (
    <button
      type="button"
      className={cn(styles.assetPreviewAddButton, { [styles.assetPreviewAddButtonCompact]: compact })}
      disabled={adding}
      onClick={handleClick}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {label}
    </button>
  );
}
/**
 * 新素材库管理确认弹窗，用于删除文件夹、删除素材和批量删除。
 */
export function AssetModalConfirmDialog({
  visible,
  title,
  note,
  confirmLabel,
  onClose,
  onConfirm,
}: AssetModalConfirmDialogProps) {
  const languageStore = useLanguageStore((s) => s.languageStore);
  if (!visible) return null;
  return (
    <div className={styles.manageDialogOverlay} role="dialog" aria-modal="true">
      <button
        type="button"
        className={styles.manageDialogBackdrop}
        onClick={onClose}
        aria-label={languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_sidebar_close)}
        tabIndex={-1}
      />
      <div className={cn(styles.manageDialog, styles.confirmDialog)}>
        <div className={styles.confirmDialogBody}>
          <h2 className={styles.manageDialogTitle}>{title}</h2>
          <p className={styles.confirmDialogNote}>{note}</p>
        </div>
        <div className={styles.manageDialogFooter}>
          <button type="button" className={styles.manageDialogCancel} onClick={onClose}>
            {languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_toast_cancel)}
          </button>
          <button type="button" className={styles.manageDialogSubmit} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
/**
 * 判断移动弹窗中的文件夹是否可见：父链任一节点折叠时隐藏当前节点。
 */
export function isMoveDialogFolderVisible(
  folder: FolderItem,
  folderById: Map<string, FolderItem>,
  expandedFolderIds: Set<string>,
): boolean {
  let parentId = getFolderParentNumericId(folder);
  const visitedParentIds = new Set<string>();
  while (parentId > 0) {
    const parentKey = String(parentId);
    if (visitedParentIds.has(parentKey)) return true;
    visitedParentIds.add(parentKey);
    if (!expandedFolderIds.has(parentKey)) return false;
    const parentFolder = folderById.get(parentKey);
    if (!parentFolder) return true;
    parentId = getFolderParentNumericId(parentFolder);
  }
  return true;
}
/**
 * 基于当前扁平文件夹列表反推哪些节点真实有子节点，避免叶子节点误显示展开按钮。
 */
export function getMoveDialogExpandableFolderIds(folders: FolderItem[]): Set<string> {
  const folderIds = new Set(folders.map((folder) => String(getFolderNumericId(folder) ?? folder.id)));
  const expandableFolderIds = new Set<string>();
  folders.forEach((folder) => {
    const parentId = getFolderParentNumericId(folder);
    const parentKey = String(parentId);
    if (parentId > 0 && folderIds.has(parentKey)) {
      expandableFolderIds.add(parentKey);
    }
  });
  return expandableFolderIds;
}
/**
 * 资产库文件夹选择弹框，供移动文件夹/素材以及保存到资产库复用。
 */
export function AssetModalMoveDialog({
  visible,
  folders,
  targetFolderId,
  overlayZIndex,
  disabledFolderReasons,
  createFolderDisabledReason,
  title,
  confirmLabel,
  onTargetFolderChange,
  onCreateFolder,
  onClose,
  onSubmit,
}: AssetModalMoveDialogProps) {
  const languageStore = useLanguageStore((s) => s.languageStore);
  const unnamedFolderText = languageStore.smartcatTranslationText((value) => value.unnamed_folder);
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<string>>(() => new Set());
  const folderById = useMemo(
    () => new Map(folders.map((folder) => [String(getFolderNumericId(folder) ?? folder.id), folder])),
    [folders],
  );
  const folderIdsWithChildren = useMemo(() => getMoveDialogExpandableFolderIds(folders), [folders]);
  const visibleFolders = useMemo(
    () => folders.filter((folder) => isMoveDialogFolderVisible(folder, folderById, expandedFolderIds)),
    [expandedFolderIds, folderById, folders],
  );
  useLayoutEffect(() => {
    if (!visible) return;
    setExpandedFolderIds(new Set(folderIdsWithChildren));
  }, [folderIdsWithChildren, visible]);
  /** 切换移动弹窗中的文件夹展开状态，只影响树展示，不改变当前移动目标。 */
  const handleToggleFolderExpand = useCallback((folderId: string) => {
    setExpandedFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  }, []);
  if (!visible) return null;
  return (
    <div
      className={cn(styles.manageDialogOverlay, styles.moveDialogOverlay)}
      style={overlayZIndex != null ? { zIndex: overlayZIndex } : undefined}
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        className={styles.manageDialogBackdrop}
        onClick={onClose}
        aria-label={languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_sidebar_close)}
        tabIndex={-1}
      />
      <div className={cn(styles.manageDialog, styles.moveDialog)}>
        <div className={styles.manageDialogHeader}>
          <h2 className={styles.manageDialogTitle}>
            {title ?? languageStore.smartcatTranslationText((v) => v.move_to_)}
          </h2>
          <button
            type="button"
            className={styles.manageDialogClose}
            onClick={onClose}
            aria-label={languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_sidebar_close)}
          >
            <IconFont name="crossBlack" fontSize={20} color="currentColor" />
          </button>
        </div>
        <div
          className={styles.moveFolderList}
          role="listbox"
          aria-label={languageStore.smartcatTranslationText((v) => v.please_select_a_folder)}
        >
          {visibleFolders.map((folder) => {
            const id = getFolderNumericId(folder);
            const folderKey = String(getFolderNumericId(folder) ?? folder.id);
            const selected = id != null && targetFolderId === id;
            const depth = Number(folder.depth) || 1;
            const hasChildren = folderIdsWithChildren.has(folderKey);
            const expanded = expandedFolderIds.has(folderKey);
            const disabledReason = id != null ? disabledFolderReasons?.get(id) : undefined;
            const disabled = Boolean(disabledReason);
            // 叶子节点只用缩进补齐展开列，DOM 中不保留空按钮，避免出现深色占位块。
            const paddingLeft = Math.min(8 + (depth - 1) * 16 + (hasChildren ? 0 : 24), 128);
            return (
              <div
                key={folder.id}
                className={cn(styles.moveFolderItem, {
                  [styles.moveFolderItemSelected]: selected,
                  [styles.moveFolderItemDisabled]: disabled,
                })}
                style={{ paddingLeft }}
                title={disabledReason || folder.name || unnamedFolderText}
              >
                {hasChildren ? (
                  <button
                    type="button"
                    className={styles.moveFolderExpand}
                    aria-label={
                      expanded
                        ? languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_sidebar_close)
                        : languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_sidebar_open)
                    }
                    aria-expanded={expanded}
                    onClick={() => handleToggleFolderExpand(folderKey)}
                  >
                    <IconFont
                      name={expanded ? 'chevronDownFill' : 'chevronRightBold'}
                      fontSize={8}
                      color="currentColor"
                    />
                  </button>
                ) : null}
                <button
                  type="button"
                  className={styles.moveFolderSelect}
                  role="option"
                  aria-selected={selected}
                  aria-disabled={disabled}
                  onClick={() => {
                    if (disabledReason) {
                      message.info(disabledReason);
                      return;
                    }
                    if (id != null) onTargetFolderChange(id);
                  }}
                >
                  <span className={styles.moveFolderIcon} aria-hidden="true">
                    <IconFont name="folderBold" fontSize={20} color="currentColor" />
                  </span>
                  <span className={styles.moveFolderName}>{folder.name || unnamedFolderText}</span>
                  {selected && (
                    <IconFont
                      name="checkV2Fill"
                      fontSize={10}
                      color="var(--content-primary)"
                      className={styles.moveFolderCheck}
                    />
                  )}
                </button>
              </div>
            );
          })}
        </div>
        <div className={cn(styles.manageDialogFooter, styles.moveDialogFooter)}>
          {onCreateFolder ? (
            <button
              type="button"
              className={styles.moveFolderCreateBtn}
              disabled={Boolean(createFolderDisabledReason)}
              title={createFolderDisabledReason}
              onClick={onCreateFolder}
            >
              <IconFont name="plusBold" fontSize={16} color="currentColor" />
              <span>{languageStore.smartcatTranslationText((v) => v.new_folder)}</span>
            </button>
          ) : (
            <span />
          )}
          <div className={styles.moveDialogFooterActions}>
            <button type="button" className={styles.manageDialogCancel} onClick={onClose}>
              {languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_toast_cancel)}
            </button>
            <button
              type="button"
              className={styles.manageDialogSubmit}
              disabled={targetFolderId == null}
              onClick={onSubmit}
            >
              {confirmLabel ?? languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_toast_confirm)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
export interface MediaLibraryAudioWaveformProps {
  playing: boolean;
  progress: number;
  onSeek?: (progress: number) => void;
}
/**
 * 渲染资产库音频卡片波形；播放中按真实进度将左侧柱切为高亮色，并支持拖拽指针 seek。
 */
export function MediaLibraryAudioWaveform({ playing, progress, onSeek }: MediaLibraryAudioWaveformProps) {
  const coverRef = useRef<HTMLDivElement | null>(null);
  const [dragging, setDragging] = useState(false);
  const seekable = playing && Boolean(onSeek);
  const safeProgress = playing ? Math.min(1, Math.max(0, progress || 0)) : 0;
  const playedBarCount = Math.round(MEDIA_LIBRARY_AUDIO_WAVEFORM_BARS.length * safeProgress);
  const progressStyle = {
    '--audio-progress': `${safeProgress * 100}%`,
  } as React.CSSProperties;
  /** 将指针横向位置换算成 0-1 的音频播放进度。 */
  const seekByPointer = useCallback(
    (clientX: number) => {
      const rect = coverRef.current?.getBoundingClientRect();
      if (!rect || rect.width <= 0 || !onSeek) return;
      const nextProgress = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      onSeek(nextProgress);
    },
    [onSeek],
  );
  /** 按下波形进度控制器后开始拖拽，并立即同步当前位置。 */
  const handleSeekPointerDown = useCallback(
    (e: React.PointerEvent<HTMLSpanElement>) => {
      if (!seekable) return;
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.setPointerCapture(e.pointerId);
      setDragging(true);
      seekByPointer(e.clientX);
    },
    [seekByPointer, seekable],
  );
  /** 拖拽波形进度控制器时持续同步隐藏 audio 的 currentTime。 */
  const handleSeekPointerMove = useCallback(
    (e: React.PointerEvent<HTMLSpanElement>) => {
      if (!dragging || !seekable) return;
      e.preventDefault();
      e.stopPropagation();
      seekByPointer(e.clientX);
    },
    [dragging, seekByPointer, seekable],
  );
  /** 松开或取消拖拽时释放进度控制器的 pointer capture。 */
  const handleSeekPointerEnd = useCallback(
    (e: React.PointerEvent<HTMLSpanElement>) => {
      if (!dragging) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      setDragging(false);
    },
    [dragging],
  );
  /** 阻止拖拽控制器点击事件冒泡到资产卡片，避免误触添加或预览。 */
  const handleSeekClick = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>) => {
      if (!seekable) return;
      e.preventDefault();
      e.stopPropagation();
    },
    [seekable],
  );
  return (
    <div
      ref={coverRef}
      className={cn(styles.mediaLibraryAudioCover, {
        [styles.mediaLibraryAudioCoverPlaying]: playing,
        [styles.mediaLibraryAudioCoverSeekable]: seekable,
        [styles.mediaLibraryAudioCoverDragging]: dragging,
      })}
      style={progressStyle}
      aria-hidden
    >
      <div className={styles.mediaLibraryAudioWaveform}>
        {MEDIA_LIBRARY_AUDIO_WAVEFORM_BARS.map((bar, index) => (
          <span
            key={bar.id}
            className={cn(styles.mediaLibraryAudioWaveBar, {
              [styles.mediaLibraryAudioWaveBarPlayed]: playing && index < playedBarCount,
            })}
            style={{ height: bar.height }}
          />
        ))}
      </div>
      <span className={styles.mediaLibraryAudioPlayhead} />
      <span
        className={styles.mediaLibraryAudioCursor}
        onPointerDown={handleSeekPointerDown}
        onPointerMove={handleSeekPointerMove}
        onPointerUp={handleSeekPointerEnd}
        onPointerCancel={handleSeekPointerEnd}
        onClick={handleSeekClick}
      />
    </div>
  );
}
/**
 * 资产弹层卡片封面（非文档类）：图/音视频载入或解码完成前覆盖高光扫光层。
 */
export const AssetModalMediaCover = React.memo(
  (props: AssetModalMediaCoverProps) => {
    const {
      item,
      coverVoiceArtSrc,
      showAudioWaveform = false,
      preserveImageAspectRatio = false,
      audioPlayingId,
      audioProgress = 0,
      onAudioSeek,
    } = props;
    const [mediaDecoded, setMediaDecoded] = useState(false);
    const videoCoverInitialSrc = useMemo(() => {
      if (item.materialType !== 'video' || !item.mediaUrl?.trim()) {
        return '';
      }
      return resolveAssetModalVideoCoverSrc(item);
    }, [item]);
    const [videoCoverSrc, setVideoCoverSrc] = useState(videoCoverInitialSrc);
    const videoCoverSrcRef = useRef(videoCoverSrc);
    videoCoverSrcRef.current = videoCoverSrc;
    const isWaveformAudioItem = showAudioWaveform && item.materialType === 'audio';
    const needsShimmer = (!isWaveformAudioItem && item.materialType === 'audio')
      || (item.materialType === 'video' && Boolean(item.mediaUrl?.trim()))
      || Boolean(item.coverUrl?.trim());
    useEffect(() => {
      setMediaDecoded(false);
      setVideoCoverSrc(videoCoverInitialSrc);
    }, [item.id, item.coverUrl, item.mediaUrl, item.materialType, coverVoiceArtSrc, videoCoverInitialSrc]);

    /**
     * 视频封面加载失败时切换统一兜底图；兜底仍失败则结束扫光避免卡住。
     */
    const handleVideoCoverError = useCallback(() => {
      if (videoCoverSrcRef.current === VIDEO_COVER_FALLBACK_URL) {
        setMediaDecoded(true);
        return;
      }
      setVideoCoverSrc(VIDEO_COVER_FALLBACK_URL);
    }, []);
    const showShimmer = needsShimmer && !mediaDecoded;
    if (item.materialType === 'audio') {
      if (isWaveformAudioItem) {
        return (
          <div className={styles.assetCoverStack}>
            <MediaLibraryAudioWaveform
              playing={audioPlayingId === item.id}
              progress={audioProgress}
              onSeek={
                onAudioSeek ? (nextProgress) => onAudioSeek(item.id, nextProgress) : undefined
              }
            />
          </div>
        );
      }
      return (
        <div className={styles.assetCoverStack}>
          <div className={styles.audioCover}>
            <div className={styles.audioCoverBase} aria-hidden />
            <NextImage
              src={coverVoiceArtSrc}
              alt=""
              width={160}
              sizes="300"
              className={styles.audioCoverArt}
              height="100%"
              onLoad={() => setMediaDecoded(true)}
              onError={() => setMediaDecoded(true)}
            />
          </div>
          {showShimmer ? <div className={styles.mediaCoverShimmer} aria-hidden /> : null}
        </div>
      );
    }
    if (item.materialType === 'video' && item.mediaUrl?.trim()) {
      return (
        <div className={styles.assetCoverStack}>
          <NextImage
            src={videoCoverSrc}
            alt=""
            className={styles.coverImg}
            width={160}
            sizes="160px"
            loading="lazy"
            onLoad={() => setMediaDecoded(true)}
            onError={handleVideoCoverError}
          />
          {showShimmer ? <div className={styles.mediaCoverShimmer} aria-hidden /> : null}
        </div>
      );
    }
    if (item.coverUrl) {
      const thumbnailUrl = getThumbnailUrl(item.coverUrl, 320);
      return (
        <div className={styles.assetCoverStack}>
          <NextImage
            src={thumbnailUrl}
            alt=""
            className={cn(styles.coverImg, {
              [styles.mediaLibraryCoverImg]: preserveImageAspectRatio && item.materialType === 'image',
            })}
            width={160}
            sizes="160px"
            loading="lazy"
            onLoad={() => setMediaDecoded(true)}
            onError={() => setMediaDecoded(true)}
          />
          {showShimmer ? <div className={styles.mediaCoverShimmer} aria-hidden /> : null}
        </div>
      );
    }
    return <div className={styles.placeholder} aria-hidden />;
  },
  (prevProps, nextProps) => {
    // 音频进度高频变化只影响正在播放的音频卡片，其他卡片跳过该 prop 比较
    const isAudioCard = prevProps.item.materialType === 'audio' && prevProps.showAudioWaveform;
    if (!isAudioCard && prevProps.audioProgress !== nextProps.audioProgress) {
      // 非音频卡片忽略 progress 变化，比较其余 props
      return (
        prevProps.item === nextProps.item
        && prevProps.coverVoiceArtSrc === nextProps.coverVoiceArtSrc
        && prevProps.showAudioWaveform === nextProps.showAudioWaveform
        && prevProps.preserveImageAspectRatio === nextProps.preserveImageAspectRatio
        && prevProps.audioPlayingId === nextProps.audioPlayingId
        && prevProps.onAudioSeek === nextProps.onAudioSeek
      );
    }
    return (
      prevProps.item === nextProps.item
      && prevProps.coverVoiceArtSrc === nextProps.coverVoiceArtSrc
      && prevProps.showAudioWaveform === nextProps.showAudioWaveform
      && prevProps.preserveImageAspectRatio === nextProps.preserveImageAspectRatio
      && prevProps.audioPlayingId === nextProps.audioPlayingId
      && prevProps.audioProgress === nextProps.audioProgress
      && prevProps.onAudioSeek === nextProps.onAudioSeek
    );
  },
);
export type AssetModalItemRootProps = React.PropsWithChildren<{
  item: AssetItemView;
  canvasDragEnabled: boolean;
  className: string;
  style: React.CSSProperties;
  role?: 'button';
  tabIndex: number;
  'aria-label': string;
  'aria-pressed'?: boolean;
  'aria-disabled'?: boolean;
  /** 卡片首次进入视口时触发，用于列表卡片曝光埋点。 */
  onExposed?: (item: AssetItemView) => void;
  onClick: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
}>;
/**
 * 资产库瀑布流卡片根节点：画布场景下支持拖到 InfiniteCanvas 落点插入（ahooks useDrag / useDrop）。
 *
 * @param props.item 列表项
 * @param props.canvasDragEnabled 是否注册可拖入画布的拖拽数据
 * @param props.children 卡内层内容
 */
export function AssetModalItemRoot(props: AssetModalItemRootProps) {
  const {
    item,
    canvasDragEnabled,
    children,
    className,
    style,
    role,
    tabIndex,
    'aria-label': ariaLabel,
    'aria-pressed': ariaPressed,
    'aria-disabled': ariaDisabled,
    onExposed,
    onClick,
    onKeyDown,
  } = props;
  const dragRef = useRef<HTMLDivElement>(null);
  const exposureTrackRef = useRef({ item, onExposed });
  exposureTrackRef.current = { item, onExposed };
  const exposureRef = useCardExpo<HTMLDivElement>({
    isActive: Boolean(onExposed),
    onExpo: () => {
      const latestTrack = exposureTrackRef.current;
      latestTrack.onExposed?.(latestTrack.item);
    },
  });
  const mutableExposureRef = exposureRef as React.MutableRefObject<HTMLDivElement | null>;
  /** 同一个卡片根节点同时交给拖拽和卡片曝光 Hook。 */
  const setRootRef = useCallback((element: HTMLDivElement | null) => {
    dragRef.current = element;
    mutableExposureRef.current = element;
  }, [mutableExposureRef]);
  const dragPayload = useMemo(() => {
    if (!canvasDragEnabled) {
      return DISABLED_ASSET_CANVAS_DRAG_PAYLOAD;
    }
    const isVideo = item.materialType === 'video';
    const ext = getImageExtensionFromUrl(item.mediaUrl).extension || 'png';
    const cover = isVideo ? getAssetVideoPosterUrl(item) : item.coverUrl || item.mediaUrl;
    return {
      type: isVideo ? ('video' as const) : ('image' as const),
      url: item.mediaUrl,
      uri: item.mediaUrl,
      coverUrl: cover,
      suffix: ext,
      name: (item.raw.name || '').trim() || `asset-${item.id}`,
      insertPos: 'point' as const,
      extraData: {
        role: 'user',
        source: 'asset_library',
        asset_id: item.raw.id,
      },
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 保持拆分前按拖拽 payload 实际字段失效的行为
  }, [canvasDragEnabled, item.coverUrl, item.id, item.materialType, item.mediaUrl, item.raw.id, item.raw.name]);
  useDrag(dragPayload, dragRef, {
    // eslint-disable-next-line consistent-return
    onDragStart: (ev: React.DragEvent) => {
      if (!canvasDragEnabled) {
        ev.preventDefault();
        return false;
      }
      try {
        if (dragRef.current) {
          ev.dataTransfer.setDragImage(dragRef.current, 0, 0);
        }
      } catch (err) {
        console.warn('[AssetModalItemRoot] setDragImage failed', err);
      }
      return undefined;
    },
  });
  return (
    <div
      ref={setRootRef}
      className={className}
      style={style}
      role={role}
      tabIndex={tabIndex}
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
      aria-disabled={ariaDisabled}
      onClick={onClick}
      onKeyDown={onKeyDown}
    >
      {children}
    </div>
  );
}
/**
 * 读取文件夹 id 的数字形态，过滤接口异常返回，避免传给文件夹接口非法 id。
 */
export function getFolderNumericId(folder?: FolderItem | null): number | undefined {
  const folderId = Number(folder?.id);
  return Number.isFinite(folderId) && folderId > 0 ? folderId : undefined;
}
/**
 * 读取文件夹 parent_id 的数字形态，根节点统一返回 0。
 */
export function getFolderParentNumericId(folder?: FolderItem | null): number {
  const parentId = Number(folder?.parentId ?? 0);
  return Number.isFinite(parentId) && parentId > 0 ? parentId : 0;
}
/**
 * 素材库媒体卡标题，优先沿用接口 name，缺省时退回资源 URL 和 id。
 */
export function getMediaLibraryAssetTitle(item: AssetItemView): string {
  return assetDisplayTitle(item.raw, item.mediaUrl, item.id);
}
/**
 * 素材库卡片副信息右侧文案：优先显示真实文件后缀，缺省时回退素材类型。
 */
export function getMediaLibraryAssetFormatLabel(item: AssetItemView): string {
  const ext = (item.fileExtension || '').trim();
  if (ext) return ext.toUpperCase();
  if (item.materialType === 'audio') return 'MP3';
  if (item.materialType === 'video') return 'MP4';
  return item.materialType === 'image' ? 'PNG' : '';
}
/**
 * 构造资源弹窗单项下载文件名，优先补齐列表里显示的真实后缀。
 */
export function getAssetModalDownloadFilename(item: AssetItemView): string {
  const title = getMediaLibraryAssetTitle(item).trim() || 'asset';
  const ext = getMediaLibraryAssetFormatLabel(item).toLowerCase();
  if (!ext || title.toLowerCase().endsWith(`.${ext}`)) return title;
  return `${title}.${ext}`;
}
/**
 * 截断资产库名称到服务端允许的 20 字，超长名称直接按文档要求截断而不 toast。
 */
export function truncateMediaLibraryName(value: string): string {
  return Array.from(value.trim()).slice(0, MEDIA_LIBRARY_MAX_NAME_LENGTH).join('');
}
/**
 * 去掉常见文件扩展名后生成资产库保存名称，空名称时使用兜底前缀。
 */
export function normalizeMediaLibrarySaveName(value: string | undefined, fallback: string): string {
  const withoutExt = String(value || '')
    .replace(/\.[^/.]+$/, '')
    .trim();
  return truncateMediaLibraryName(withoutExt || fallback) || fallback;
}
/**
 * 将可选数字字段转成有效正数，过滤接口或上传回调里的空值。
 */
export function normalizePositiveNumber(value: unknown): number | undefined {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : undefined;
}
/** 从 Range 响应的 Content-Range 中读取远程文件总字节数。 */
export function parseRemoteAssetContentRangeSize(value: string | null): number | undefined {
  return normalizePositiveNumber(value?.match(/\/(\d+)$/)?.[1]);
}
/** 历史记录未携带文件大小时，按正常资产保存链路从远程响应头补齐。 */
export async function fetchRemoteHistoryAssetFileSize(url: string): Promise<number | undefined> {
  if (typeof fetch !== 'function' || !url) return undefined;
  const controller = typeof AbortController === 'function' ? new AbortController() : undefined;
  const timer = controller ? globalThis.setTimeout(() => controller.abort(), 3000) : undefined;
  try {
    const response = await fetch(url, {
      method: 'HEAD',
      signal: controller?.signal,
    });
    const contentLength = normalizePositiveNumber(response.headers.get('content-length'));
    if (contentLength) return contentLength;
  } catch (_err) {
    // 部分 CDN 禁止 HEAD，请求失败时继续尝试 Range。
  } finally {
    if (timer) globalThis.clearTimeout(timer);
  }
  try {
    const response = await fetch(url, { headers: { Range: 'bytes=0-0' } });
    return parseRemoteAssetContentRangeSize(response.headers.get('content-range'));
  } catch (_err) {
    return undefined;
  }
}
/**
 * 安全解析 JSON 字符串字段，主要用于复用历史记录资产里的 ext / gen_params。
 */
export function parseMediaLibraryRecordJson(value: unknown): Record<string, any> {
  if (!value) return {};
  if (typeof value === 'object' && !Array.isArray(value)) return value as Record<string, any>;
  if (typeof value !== 'string') return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}
/**
 * 将对象序列化为接口要求的 JSON 字符串；空对象不传，减少无意义字段。
 */
export function stringifyMediaLibraryRecord(value: Record<string, any>): string | undefined {
  const entries = Object.entries(value).filter(([, v]) => v != null && v !== '');
  if (!entries.length) return undefined;
  return JSON.stringify(Object.fromEntries(entries));
}
/**
 * 判断对象是否有可用字段，供历史记录参数兜底提取使用。
 */
export function hasMediaLibraryRecordValue(value: Record<string, any>): boolean {
  return Object.keys(value).length > 0;
}
/**
 * 从多个候选字段中读取第一份有效 JSON 对象，兼容字符串和已解析对象。
 */
export function firstMediaLibraryRecord(...values: unknown[]): Record<string, any> | undefined {
  for (const value of values) {
    const record = parseMediaLibraryRecordJson(value);
    if (hasMediaLibraryRecordValue(record)) {
      return record;
    }
  }
  return undefined;
}
/**
 * 从历史记录 raw 中恢复 mcpInfo，兼容 mcp_info、node_data 和单独 parameters 字段。
 */
export function resolveHistoryMediaLibraryMcpInfo(
  raw: Record<string, any>,
  nodeData?: Record<string, any>,
): Record<string, any> | undefined {
  const mcpInfo = firstMediaLibraryRecord(raw.mcpInfo, raw.mcp_info, nodeData?.mcpInfo, nodeData?.mcp_info);
  const parameters = firstMediaLibraryRecord(
    mcpInfo?.parameters,
    raw.parameters,
    raw.params,
    raw.arguments,
    raw.args,
    raw.request_params,
    nodeData?.parameters,
  );
  if (!mcpInfo && !parameters) {
    return undefined;
  }
  const baseMcpInfo = mcpInfo ?? {};
  return {
    ...baseMcpInfo,
    api_name: String(baseMcpInfo.api_name ?? raw.api_name ?? nodeData?.api_name ?? ''),
    model_id: String(baseMcpInfo.model_id ?? raw.model_id ?? nodeData?.model_id ?? ''),
    node_model_id: String(baseMcpInfo.node_model_id ?? raw.node_model_id ?? nodeData?.node_model_id ?? ''),
    parameters: parameters ?? {},
  };
}
/**
 * 历史记录保存到新素材库时补齐 gen_params，避免旧接口未给标准 gen_params 时丢失提示词。
 */
export function buildMediaLibraryHistoryGenParams(raw: Record<string, any>): string | undefined {
  const rawGenParams = raw.gen_params ?? raw.genParams;
  const parsedGenParams = parseMediaLibraryRecordJson(rawGenParams);
  const existingGenParams = hasMediaLibraryRecordValue(parsedGenParams)
    ? typeof rawGenParams === 'string'
      ? rawGenParams
      : stringifyMediaLibraryRecord(parsedGenParams)
    : undefined;
  if (existingGenParams) {
    return existingGenParams;
  }
  const nodeData = firstMediaLibraryRecord(raw.node_data, raw.nodeData) ?? {};
  const mcpInfo = resolveHistoryMediaLibraryMcpInfo(raw, nodeData);
  const mediaList = Array.isArray(raw.media_list)
    ? raw.media_list
    : Array.isArray(nodeData.media_list)
      ? nodeData.media_list
      : undefined;
  const normalizedNodeData = {
    ...nodeData,
    ...(mcpInfo ? { mcpInfo } : {}),
    ...(mediaList?.length ? { media_list: mediaList } : {}),
  };
  return stringifyMediaLibraryRecord({
    ...(mcpInfo ? { mcpInfo, parameters: mcpInfo.parameters } : {}),
    ...(mediaList?.length ? { media_list: mediaList } : {}),
    ...(hasMediaLibraryRecordValue(normalizedNodeData) ? { node_data: normalizedNodeData } : {}),
  });
}
/**
 * 从 URL 或文件名中读取扩展名，图片优先复用现有 URL 解析工具。
 */
export function getMediaLibrarySaveFileExtension(url: string, filename?: string): string {
  const fromImageUrl = getImageExtensionFromUrl(url).extension;
  if (fromImageUrl) return fromImageUrl.toLowerCase();
  const source = `${filename || ''} ${url}`;
  const match = source.match(/\.([a-z0-9]+)(?:[?#\s]|$)/i);
  return match?.[1]?.toLowerCase() || '';
}
/**
 * 将资产弹窗视图项的素材类型限制为资产库弹窗上传支持的图片、视频、音频。
 */
export function resolveAssetViewMediaLibraryMaterialType(
  item: AssetItemView,
): MediaLibraryUploadMaterialType | undefined {
  if (item.materialType === 'image' || item.materialType === 'video' || item.materialType === 'audio') {
    return item.materialType;
  }
  return undefined;
}
/**
 * 历史记录资产选择后，补齐文件大小并构造保存到当前资产库文件夹的接口参数。
 */
export async function buildMediaLibrarySaveParamsFromHistoryItem(
  item: AssetItemView,
  folderId: number,
): Promise<MediaLibraryItemSaveParams | null> {
  const materialType = resolveAssetViewMediaLibraryMaterialType(item);
  const url = item.mediaUrl?.trim();
  if (!materialType || !url) return null;
  const raw = item.raw as Record<string, any>;
  const rawExt = parseMediaLibraryRecordJson(raw.ext);
  const fileExtension = item.fileExtension || rawExt.file_extension || getMediaLibrarySaveFileExtension(url, raw.name);
  const genParams = buildMediaLibraryHistoryGenParams(raw);
  const compressUrl = materialType === 'video' ? getAssetVideoCompressUrl(item) : '';
  const watermarkCompressUrl = materialType === 'video' ? getAssetVideoWatermarkCompressUrl(item) : '';
  const thumbnailUrl = materialType === 'image'
    ? item.coverUrl || url
    : materialType === 'video'
      ? getAssetVideoPosterUrl(item)
      : item.coverUrl || raw.thumbnail_url || raw.cover_url || raw.coverUrl;
  const knownFileSize = [
    raw.file_size,
    raw.fileSize,
    raw.file_byte,
    raw.fileByte,
    raw.file_bytes,
    raw.fileBytes,
    raw.bytes,
    raw.size,
    raw.image?.size,
    raw.video?.size,
    raw.audio?.size,
    raw.file?.size,
    rawExt.file_size,
    rawExt.size,
  ]
    .map(normalizePositiveNumber)
    .find((size) => size != null);
  const fileSize = knownFileSize ?? (await fetchRemoteHistoryAssetFileSize(url));
  return {
    folder_id: folderId,
    name: normalizeMediaLibrarySaveName(assetDisplayTitle(item.raw, item.mediaUrl, item.id), `asset-${item.id}`),
    material_type: materialType,
    url,
    thumbnail_url: thumbnailUrl || undefined,
    gen_params: genParams,
    file_size: fileSize,
    ext: stringifyMediaLibraryRecord({
      ...rawExt,
      file_extension: fileExtension,
      width: normalizePositiveNumber(raw.width ?? raw.image?.width ?? raw.video?.width),
      height: normalizePositiveNumber(raw.height ?? raw.image?.height ?? raw.video?.height),
      duration: raw.duration ?? rawExt.duration,
      source_id: raw.source_id ?? raw.sourceId ?? raw.id ?? item.id,
      task_id: raw.task_id ?? raw.taskId ?? raw.source_id ?? raw.sourceId,
      sub_task_id: raw.sub_task_id ?? raw.subTaskId,
      ...(compressUrl ? { compress_url: compressUrl } : {}),
      ...(watermarkCompressUrl ? { watermark_compress_url: watermarkCompressUrl } : {}),
      source_from: 'history_list',
      content_source: 'history_list',
    }),
  };
}
